import { Box, Flex, Text, Button, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ISelectedPlanAddOn } from 'containers/AccountBilling/UpgradeBilling/UpgradeBilling';
import { PLANS } from 'global/plans';
import { routePaths } from 'global/routePaths';
import { ISwitchPlanData, useSwitchPlan } from 'hooks/accountBilling/useSwitchPlan';
import { useCheckoutStore } from 'store/useCheckoutStore';

import TermsAndConditionModal from '../TermsAndConditionModal';

interface IPriceCalculator {
  selectedPlan: string;
  noOfEmployees: number;
  costPerUser: number;
  isYearly: boolean;
  isCurrentPlan: boolean;
  addOns: ISelectedPlanAddOn[];
  addOnsChanged?: boolean;
  isAlreadyCustomer?: boolean;
}

const plans = [PLANS.basic, PLANS.pro, PLANS.premium];

const PriceCalculator: React.FC<IPriceCalculator> = ({
  selectedPlan,
  noOfEmployees,
  costPerUser,
  isYearly,
  isCurrentPlan,
  addOns,
  addOnsChanged,
  isAlreadyCustomer = false,
}) => {
  const { t } = useTranslation(['accountBilling', 'common']);

  const { isOpen: tAndCModalIsOpen, onOpen: tAndCModalOnOpen, onClose: tAndCModalOnClose } = useDisclosure();

  const { addCheckoutDetails } = useCheckoutStore(state => state);

  const navigate = useNavigate();

  const { status: switchPlansStatus, mutateAsync: switchPlansFn } = useSwitchPlan();

  const planAddOnPrices = () => {
    let addOnPrice = 0;
    for (let i = 0; i < addOns.length; i++) {
      addOnPrice += addOns[i]?.price;
    }
    return addOnPrice || 0;
  };

  const finalCostPerUser = costPerUser + planAddOnPrices();

  const handleUpgrade = () => {
    const planInfo = {
      name: selectedPlan,
      duration: isYearly ? 'annual' : 'monthly',
      rate: finalCostPerUser,
      noOfUsers: noOfEmployees,
      totalRate: finalCostPerUser * noOfEmployees || 0,
      addOns: addOns,
      addOnsPrice: planAddOnPrices(),
    };
    addCheckoutDetails(planInfo);
    navigate(routePaths.accountBilling.checkout);
  };

  const handleUpgradeAlreadyCustomer = () => {
    tAndCModalOnOpen();
  };

  const onAcceptTAndC = () => {
    const postData: ISwitchPlanData = {
      plan: selectedPlan,
      duration: isYearly ? 'annual' : 'monthly',
      add_ons: addOns.map(addOn => addOn?.id) || [],
    };
    switchPlansFn(postData).then(() => {
      navigate(routePaths.accountBilling.base.route);
      window.location.reload();
    });
  };

  return (
    <>
      <Flex flexDir={['column', null, 'row']}>
        <Box bg="newBlue.100" flex={1} px={10} py={12} borderTopLeftRadius="base" borderBottomLeftRadius="base">
          <Flex gridGap={2.5} mb={9}>
            {plans.map((plan, index: number) => (
              <Box
                sx={{
                  bg: 'white',
                  color: 'gray.500',
                  px: '4',
                  py: '2.5',
                  border: '1px solid',
                  borderColor: 'primary.100',
                  borderRadius: 'lg',
                  textTransform: 'capitalize',
                  ...(selectedPlan?.toLowerCase() === plan && {
                    borderColor: 'newOrange.1000',
                    color: 'newOrange.1000',
                  }),
                }}
                key={index}
              >
                {plan}
              </Box>
            ))}
          </Flex>
          <Text fontWeight="600" fontSize="md">
            {t('accountBilling:noOfEmployeesYouHave')}
          </Text>
          <Text fontWeight="600" fontSize="4xl">
            {noOfEmployees}
          </Text>
        </Box>
        <Flex
          flexDir="column"
          gridGap={6}
          bg="primary.900"
          color="white"
          flex={1}
          borderTopRightRadius="base"
          borderBottomRightRadius="base"
          px={10}
          py={12}
          sx={{
            textTransform: 'lowercase',
            '& > * > *:first-letter': {
              textTransform: 'capitalize',
            },
          }}
        >
          <Flex fontSize="md" justifyContent="space-between">
            <Text>{t('accountBilling:costPerUser')}</Text>
            <Text>${finalCostPerUser}</Text>
          </Flex>
          <Flex fontSize="md" justifyContent="space-between">
            <Text>{t('accountBilling:costForAllUsers')}</Text>
            <Text>${finalCostPerUser * noOfEmployees}</Text>
          </Flex>
          <Flex fontSize="md" justifyContent="space-between">
            <Text>
              {t('accountBilling:total')} {isYearly ? t('common:annual') : t('common:monthly')}{' '}
              {t('accountBilling:cost')}
            </Text>
            <Text>${finalCostPerUser * noOfEmployees}</Text>
          </Flex>
          {!isAlreadyCustomer ? (
            <Button colorScheme="secondary" onClick={handleUpgrade} isDisabled={isCurrentPlan && !addOnsChanged}>
              {t('accountBilling:addPaymentDetails')}
            </Button>
          ) : (
            <Button colorScheme="secondary" onClick={handleUpgradeAlreadyCustomer}>
              {t('accountBilling:upgradeNow')}
            </Button>
          )}
        </Flex>
      </Flex>
      <TermsAndConditionModal
        isOpen={tAndCModalIsOpen}
        onClose={tAndCModalOnClose}
        onAcceptTAndC={onAcceptTAndC}
        submitBtnIsLoading={switchPlansStatus === 'loading'}
      />
    </>
  );
};

export default PriceCalculator;
